import { useEffect, useState } from "react"
import { X } from "lucide-react"

interface Props {
  open: boolean
  startTime: string
  onClose: () => void
  onSave: (newStartIso: string) => Promise<void> | void
}

function toLocalInputValue(iso: string) {
  // input[type=datetime-local] expects "YYYY-MM-DDTHH:mm"
  const d = new Date(iso)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  const hh = String(d.getHours()).padStart(2, "0")
  const mi = String(d.getMinutes()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function localInputToIso(localValue: string) {
  return new Date(localValue).toISOString()
}

export default function EditStartTimeModal({ open, startTime, onClose, onSave }: Props) {
  const [localValue, setLocalValue] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setLocalValue(toLocalInputValue(startTime))
      setError(null)
    }
  }, [open, startTime])

  const handleSave = async () => {
    if (!localValue) return
    const iso = localInputToIso(localValue)

    if (new Date(iso).getTime() > Date.now()) {
      setError("Clock-in time can't be in the future.")
      return
    }

    setLoading(true)
    setError(null)
    await onSave(iso)
    setLoading(false)
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-black dark:text-white">Edit clock-in time</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Adjusts when this session started. Elapsed time and earnings update right away.
        </p>

        <input
          type="datetime-local"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-4 py-2.5 text-sm text-black dark:text-white focus:outline-none focus:border-[#2563EB] transition mb-4"
        />

        {error && <div className="text-xs text-red-600 dark:text-red-400 mb-3">{error}</div>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 border border-gray-200 dark:border-gray-700 rounded-xl py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !localValue}
            className="flex-1 bg-[#2563EB] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#1d4ed8] transition disabled:opacity-40"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  )
}
